import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfFont,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';

/**
 * Pocitani_tabulka (Math Tables) - Arithmetic practice cards
 *
 * Layout: 30 portrait pages (5 operations × 3 table sizes × 2 pages).
 * Each page: 2×2 grid of cards, each with an image label on top
 * (under a decorative arc) and a math table below.
 * Operations: +20, +100, -20, -100, ×10.
 * Table sizes: 3×3, 4×4, 5×5.
 *
 * Migrated from C# EduFrame.Img10.Generators.Pocitani_tabulka
 */

interface Op {
  operation: string;
  create: (count: number) => { top: number[]; left: number[] };
}

export class PocitaniTabulka implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  private genPlus(random: { next: (min: number, max: number) => number }, max: number): Op {
    return {
      operation: '+',
      create: (count: number) => {
        while (true) {
          const numbers = Array.from({ length: max }, (_, i) => i).filter((i) => i !== 0);
          for (let i = numbers.length - 1; i > 0; i--) {
            const j = random.next(0, i + 1);
            [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
          }

          const p1 = numbers.slice(0, count).sort((a, b) => a - b);
          const p2 = numbers.slice(count, count * 2).sort((a, b) => a - b);

          if (p1[p1.length - 1] + p2[p2.length - 1] <= max) {
            return { top: p1, left: p2 };
          }
        }
      },
    };
  }

  private genMinus(random: { next: (min: number, max: number) => number }, max: number): Op {
    return {
      operation: '\u2013', // en-dash
      create: (count: number) => {
        while (true) {
          const numbers = Array.from({ length: max }, (_, i) => i).filter((i) => i !== 0);
          for (let i = numbers.length - 1; i > 0; i--) {
            const j = random.next(0, i + 1);
            [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
          }

          const p1 = numbers.slice(0, count).sort((a, b) => a - b);
          const p2 = numbers.slice(count, count * 2).sort((a, b) => a - b);

          if (p1[0] - p2[p2.length - 1] >= 0) {
            return { top: p1, left: p2 };
          }
        }
      },
    };
  }

  private genMultiplication(
    random: { next: (min: number, max: number) => number },
    max: number
  ): Op {
    return {
      operation: '\u00d7', // ×
      create: (count: number) => {
        const numbers = Array.from({ length: max }, (_, i) => i + 1);
        for (let i = numbers.length - 1; i > 0; i--) {
          const j = random.next(0, i + 1);
          [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
        }

        const p1 = numbers.slice(0, count).sort((a, b) => a - b);
        const p2 = numbers.slice(count, count * 2).sort((a, b) => a - b);

        return { top: p1, left: p2 };
      },
    };
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const { origin, size } = calcOrigin(2, 2, margin, PdfSize.A4);
    const s = size.width / Math.sqrt(5);

    const {random} = opts.data;

    const ops: Op[] = [
      this.genPlus(random, 20),
      this.genPlus(random, 100),
      this.genMinus(random, 20),
      this.genMinus(random, 100),
      this.genMultiplication(random, 10),
    ];

    let pageIdx = 0;

    for (const op of ops) {
      for (const count of [3, 4, 5]) {
        const pages: PageGen[] = [pdf.addPage(), pdf.addPage()];

        // Shuffle image assignment
        const indexes = Array.from({ length: 10 }, (_, i) => i);
        for (let i = indexes.length - 1; i > 0; i--) {
          const j = random.next(0, i + 1);
          [indexes[i], indexes[j]] = [indexes[j], indexes[i]];
        }

        for (let p = 0; p < 2; p++) {
          const page = pages[p];

          for (let x = 0; x < 2; x++) {
            for (let y = 0; y < 2; y++) {
              const index = p * 4 + y * 2 + x;
              if (index >= opts.list.length) continue;

              const rect = new PdfRect(
                origin.x + x * size.width,
                origin.y + y * size.height,
                size.width,
                size.height
              );

              const arcW = rect.width;
              const arcRect = new PdfRect(rect.x, rect.y, arcW, arcW);

              const imgRect = new PdfRect(
                rect.x + rect.width / 2 - s / 2,
                rect.y + arcW / 2 - s,
                s,
                s
              );

              const tableRect = new PdfRect(
                rect.x,
                rect.y + arcW / 2,
                rect.width,
                rect.height - arcW / 2
              );

              page.drawRectangle(GUtil.cutPen, null, rect);

              // Image label
              if (indexes[index] < opts.list.length) {
                page.drawLabel(opts.list[indexes[index]].left, { rect: imgRect });
              }

              // Decorative arc (semicircle from 180° to 360°)
              page.drawArc(GUtil.cutPen, null, arcRect, 180, 360);

              // Math table
              const m = PdfUnit.FromMillimeters(10);
              const s1 = Math.min(
                (tableRect.width - 2 * m) / count,
                (tableRect.height - 2 * m) / count
              );

              const o1 = new PdfPoint(
                tableRect.x + (tableRect.width - s1 * count) / 2,
                tableRect.y + (tableRect.height - s1 * count) / 2
              );

              const gen = op.create(count);

              for (let x1 = 0; x1 < count; x1++) {
                for (let y1 = 0; y1 < count; y1++) {
                  const r = new PdfRect(o1.x + x1 * s1, o1.y + y1 * s1, s1, s1);

                  if (x1 !== 0 || y1 !== 0) {
                    page.drawRectangle(GUtil.basePen, null, r);
                  }

                  if (x1 === 0 && y1 === 0) {
                    page.drawString(
                      op.operation,
                      GUtil.baseFont,
                      PdfBrush.Black,
                      null,
                      r,
                      ETextAlign.Center
                    );
                  } else if (x1 === 0) {
                    page.drawString(
                      `${gen.left[y1 - 1]}`,
                      GUtil.baseFont,
                      PdfBrush.Black,
                      null,
                      r,
                      ETextAlign.Center
                    );
                  } else if (y1 === 0) {
                    page.drawString(
                      `${gen.top[x1 - 1]}`,
                      GUtil.baseFont,
                      PdfBrush.Black,
                      null,
                      r,
                      ETextAlign.Center
                    );
                  }
                }
              }
            }
          }
        }

        pageIdx += 2;
      }
    }
  }
}
